import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Tooltip from '@adsk/bim360-matrix-react-tooltip';
import { deprecateComponent } from '@adsk/bim360-matrix-react-helpers/es/deprecate';
import './_TipPopover.scss';

/**
 * Tooltip component built on Tooltip
 */
class TipPopover extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isShow: false,
      isTruncated: false
    };

    deprecateComponent('TipPopover', 'Tooltip', new Date(2020, 1, 22));
    this._setContainerRef = this._setContainerRef.bind(this);
    this._handleMouseEnter = this._handleMouseEnter.bind(this);
    this._handleMouseLeave = this._handleMouseLeave.bind(this);

    // If `autoTipOnEllipsis=true` and the `children` is wrapped by tags like `<a />`,
    // in Firefox `onMouseEnter` would be fired every time the contaienr is rendered while mouse hovering,
    // so we use a flag here to make sure `onMouseEnter` be called only once while mouse hovering.
    this._mouseHovering = false;
  }

  componentDidUpdate(_preProps, preState) {
    const isShowTooltip = this.isShowTooltip();
    const { onPopoverToggle } = this.props;

    if (onPopoverToggle && isShowTooltip !== this.isShowTooltip(preState)) {
      onPopoverToggle(isShowTooltip);
    }
  }

  componentWillUnmount() {
    this._mouseHovering = false;
    this._clearShowTimer();
    this._clearHideTimer();
  }

  isTruncated() {
    if (!this.containerRef) return false;

    let container = this.containerRef;
    // if `autoTipOnEllipsis=true` and the popover is shown, we should measure the popover node instead
    if (this.isShowTooltip()) container = this.containerRef.firstElementChild;

    return container.scrollWidth > container.offsetWidth;
  }

  isShowTooltip(state = this.state) {
    const { autoTipOnEllipsis, disabled } = this.props;
    const { isShow, isTruncated } = state;

    const usePopover = !autoTipOnEllipsis || isTruncated;
    return !disabled && isShow && usePopover;
  }

  render() {
    /* eslint-disable no-unused-vars */
    const {
      className,
      nodeClassName,
      children,
      content,
      container,
      theme,
      place,
      tooltipClassName,
      popoverMaxWidth,
      autoPlacement,
      autoTipOnEllipsis,
      // omit the following from rest
      disabled,
      showDelay,
      hideDelay,
      onPopoverToggle,
      ...rest
    } = this.props;
    /* eslint-enable no-unused-vars */

    if (!children) return null;

    const cls = cn(className, 'TipPopover', {
      'TipPopover--ellipsis': autoTipOnEllipsis
    });
    const nodeCls = cn(nodeClassName, 'TipPopover__node', {
      'TipPopover__node--ellipsis': autoTipOnEllipsis
    });
    const popoverCls = cn(
      tooltipClassName,
      'TipPopover__popover',
      `TipPopover__popover--${theme}`,
      {
        'TipPopover__popover--wrapped': !!popoverMaxWidth
      }
    );

    return (
      <div
        {...rest}
        className={cls}
        onMouseEnter={this._handleMouseEnter}
        onMouseLeave={this._handleMouseLeave}
        ref={this._setContainerRef}
      >
        {this.isShowTooltip() ? (
          <Tooltip
            className={nodeCls}
            tooltipClassName={popoverCls}
            tooltipStyle={popoverMaxWidth && { maxWidth: popoverMaxWidth }}
            content={content || children}
            container={container || this}
            place={place}
            autoTipOnEllipsis={autoTipOnEllipsis}
            show={true}
            theme={this.props.theme}
          >
            {children}
          </Tooltip>
        ) : (
          children
        )}
      </div>
    );
  }

  _setContainerRef(node) {
    this.containerRef = node;
  }

  _clearShowTimer() {
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      delete this.showTimer;
    }
  }

  _clearHideTimer() {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      delete this.hideTimer;
    }
  }

  _handleMouseEnter() {
    if (this._mouseHovering) return;
    this._mouseHovering = true;

    this._clearHideTimer();

    const { disabled, showDelay, autoTipOnEllipsis } = this.props;
    if (disabled) return;

    if (!showDelay) {
      this.setState({
        isShow: true,
        isTruncated: autoTipOnEllipsis && this.isTruncated()
      });
      return;
    }

    this.showTimer = setTimeout(() => {
      this.setState({
        isShow: true,
        isTruncated: autoTipOnEllipsis && this.isTruncated()
      });
      delete this.showTimer;
    }, showDelay);
  }

  _handleMouseLeave() {
    this._mouseHovering = false;

    this._clearShowTimer();

    const { disabled, hideDelay } = this.props;
    const { isShow } = this.state;
    if (disabled && !isShow) return;

    if (!hideDelay) {
      this.setState({
        isShow: false
      });
      return;
    }

    this.hideTimer = setTimeout(() => {
      this.setState({
        isShow: false
      });
      delete this.hideTimer;
    }, hideDelay);
  }
}

TipPopover.Themes = {
  DARK: 'dark',
  LIGHT: 'light',
  WARNING: 'warning'
};

TipPopover.defaultProps = {
  theme: TipPopover.Themes.DARK,
  autoPlacement: true,
  showDelay: 150,
  hideDelay: 100,
  autoTipOnEllipsis: false
};

TipPopover.propTypes = {
  ...Tooltip.propTypes,
  /**
   * Whether the tooltip is disabled
   */
  disabled: PropTypes.bool,
  /**
   * Class name for the container
   */
  className: PropTypes.string,
  /**
   * Class name for the node
   */
  nodeClassName: PropTypes.string,
  /**
   * The node to apply the popover relatively to
   */
  children: PropTypes.node,
  /**
   * The contents to show within the popover
   */
  content: PropTypes.node,
  /**
   * A Node, Component instance, or function that returns either. The `container` will have the Tooltip appended to it.
   */
  container: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /**
   * The theme of the tooltip
   */
  theme: PropTypes.oneOf(Object.values(Tooltip.Themes)),
  /**
   * Class name for the popover
   */
  tooltipClassName: PropTypes.string,
  /**
   * The max width of the popover
   */
  popoverMaxWidth: PropTypes.number,
  /**
   * Whether to calculate popover position based on given container
   */
  autoPlacement: PropTypes.bool,
  /**
   * Delay milliseconds to hide when mouse leave
   */
  showDelay: PropTypes.number,
  /**
   * Delay milliseconds to hide when mouse leave
   */
  hideDelay: PropTypes.number,
  /**
   * Whether to show tooltip automatically when truncated
   */
  autoTipOnEllipsis: PropTypes.bool,
  /**
   * Callback on Tooltip visibility change
   */
  onPopoverToggle: PropTypes.func
};

export default TipPopover;
